import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Card,
  CardActionArea,
  Divider,
  Typography,
} from "@mui/material";
import { Stack, Inline } from "components/layout/reusableLayouts";
import { GET_AVAILABLE_PLANS } from "graphql/platform";
import { Checkbox, Drawer } from "@outpave/ds";

type PlanOption = {
  id: string;
  name: string | null;
  description: string | null;
  /**
   * App identity keys this plan declares (feature_app_spend, feature_app_byoc, ...).
   * On an app plan this is the app's own identity. On an add-on it is the set of apps
   * the add-on belongs to; empty means the add-on is universal.
   */
  appFeatureKeys: string[];
};

type AvailablePlansData = {
  availablePlans: {
    appPlans: PlanOption[];
    addOnPlans: PlanOption[];
  };
};

interface PlanPickerDrawerProps {
  open: boolean;
  onClose: () => void;
  appPlanIds: string[];
  addOnPlanIds: string[];
  onSave: (next: { appPlanIds: string[]; addOnPlanIds: string[] }) => void;
  companyName?: string | null;
}

/** Mirrors addOnIsAllowed on the server: universal add-ons always pass. */
const addOnIsAllowed = (plan: PlanOption, allowed: Set<string>) =>
  plan.appFeatureKeys.length === 0 ||
  plan.appFeatureKeys.some((key) => allowed.has(key));

const collectAppFeatureKeys = (
  planIds: string[],
  planById: Map<string, PlanOption>,
) => {
  const keys = new Set<string>();
  planIds.forEach((id) =>
    planById.get(id)?.appFeatureKeys.forEach((key) => keys.add(key)),
  );
  return keys;
};

/**
 * One selectable plan. Apps and add-ons are the same interaction (pick N of a set),
 * so they render the same card: putting the app choice on screen next to the add-ons
 * it gates keeps the cascade visible, which a dropdown hides behind a popover.
 */
const PlanCard: React.FC<{
  title: string;
  description?: string | null;
  /** Why this card cannot be picked, e.g. "Requires Spend Management". */
  requires?: string | null;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}> = ({
  title,
  description,
  requires,
  checked,
  disabled = false,
  onToggle,
}) => (
  <Card
    variant="outlined"
    sx={{
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      ...(checked && {
        backgroundColor: "var(--color-brand-8)",
        borderColor: "var(--color-brand)",
      }),
      ...(disabled && { opacity: 0.4 }),
    }}
  >
    <CardActionArea
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={checked}
      sx={{
        justifyContent: "flex-start",
        alignItems: "stretch",
        textAlign: "left",
        width: "100%",
        padding: "var(--space-5)",
      }}
    >
      <Inline align="start" gap="var(--space-3)" wrap={false}>
        {/*
        Non-interactive: CardActionArea is already a button, and a nested button is
        invalid markup that breaks keyboard activation.
      */}
        <Checkbox
          checked={checked}
          readOnly
          disableRipple
          tabIndex={-1}
          sx={{ p: 0, mt: 0.25 }}
        />
        <Stack gap="var(--space-1)" sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {description}
            </Typography>
          )}
          {requires && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              {requires}
            </Typography>
          )}
        </Stack>
      </Inline>
    </CardActionArea>
  </Card>
);

const PlanPickerDrawer: React.FC<PlanPickerDrawerProps> = ({
  open,
  onClose,
  appPlanIds,
  addOnPlanIds,
  onSave,
  companyName,
}) => {
  const { t } = useTranslation();

  const { data, loading } = useQuery<AvailablePlansData>(GET_AVAILABLE_PLANS, {
    fetchPolicy: "cache-and-network",
    skip: !open,
  });

  const appPlans = useMemo(() => data?.availablePlans?.appPlans || [], [data]);
  const addOnPlans = useMemo(
    () => data?.availablePlans?.addOnPlans || [],
    [data],
  );

  const [localAppPlanIds, setLocalAppPlanIds] = useState<string[]>(appPlanIds);
  const [localAddOnPlanIds, setLocalAddOnPlanIds] =
    useState<string[]>(addOnPlanIds);

  useEffect(() => {
    if (open) {
      setLocalAppPlanIds(appPlanIds);
      setLocalAddOnPlanIds(addOnPlanIds);
    }
  }, [open, appPlanIds, addOnPlanIds]);

  const cleanAppName = (name: string | null | undefined, fallback: string) =>
    (name || fallback).replace(/^application\s*-\s*/i, "");

  const planById = useMemo(
    () =>
      new Map<string, PlanOption>(
        [...appPlans, ...addOnPlans].map((p) => [p.id, p]),
      ),
    [appPlans, addOnPlans],
  );

  const allowedAppFeatureKeys = useMemo(
    () => collectAppFeatureKeys(localAppPlanIds, planById),
    [localAppPlanIds, planById],
  );

  /** App identity key -> that app's display name, for the "Requires X" label. */
  const appNameByFeatureKey = useMemo(() => {
    const map = new Map<string, string>();
    appPlans.forEach((p) =>
      p.appFeatureKeys.forEach((key) =>
        map.set(key, cleanAppName(p.name, p.id)),
      ),
    );
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appPlans]);

  /**
   * Every add-on, blocked ones included, with the apps that would unlock them.
   * Blocked add-ons are shown rather than hidden so the owner can see what exists
   * and what it costs; sorting the selectable ones first keeps the actionable
   * choices at the top. Sort is stable, so catalogue order is otherwise preserved.
   */
  const decoratedAddOns = useMemo(() => {
    const decorated = addOnPlans.map((plan) => ({
      plan,
      allowed: addOnIsAllowed(plan, allowedAppFeatureKeys),
      requiredAppNames: plan.appFeatureKeys
        .map((key) => appNameByFeatureKey.get(key))
        .filter((name): name is string => Boolean(name)),
    }));
    return decorated.sort((a, b) => Number(b.allowed) - Number(a.allowed));
  }, [addOnPlans, allowedAppFeatureKeys, appNameByFeatureKey]);

  /**
   * Add-ons already on the company whose app plan is not selected. Only reachable for
   * companies whose plans predate app tagging, since the cascade below prevents new
   * orphans. Surfaced as a warning rather than auto-cleared: the server rejects them on
   * save, so the owner needs to see why.
   */
  const orphanedSelection = useMemo(
    () =>
      localAddOnPlanIds.filter((id) => {
        const plan = planById.get(id);
        if (!plan) return false;
        return !addOnIsAllowed(plan, allowedAppFeatureKeys);
      }),
    [localAddOnPlanIds, planById, allowedAppFeatureKeys],
  );

  /**
   * Deselecting an app drops the add-ons that belonged only to it.
   *
   * This lives in the change handler rather than an effect on allowedAppFeatureKeys
   * because an effect would also run on mount and on every query refetch, silently
   * clearing a legacy company's pre-existing orphans before the owner has touched
   * anything. Confining it here keeps the cascade a consequence of an explicit action.
   */
  const handleAppChange = (nextAppPlanIds: string[]) => {
    setLocalAppPlanIds(nextAppPlanIds);
    const allowed = collectAppFeatureKeys(nextAppPlanIds, planById);
    setLocalAddOnPlanIds((prev) =>
      prev.filter((id) => {
        const plan = planById.get(id);
        if (!plan) return true; // unknown plan: never strip silently
        return addOnIsAllowed(plan, allowed);
      }),
    );
  };

  // Split rather than joining on a translated conjunction: " or " is a poor i18n key,
  // and word order around a list varies by language.
  const requiresLabel = (appNames: string[]) => {
    if (appNames.length === 1)
      return t("Requires {{app}}", { app: appNames[0] });
    if (appNames.length > 1)
      return t("Requires one of {{apps}}", { apps: appNames.join(", ") });
    return t("Requires an app that is not selected");
  };

  const toggleApp = (id: string) =>
    handleAppChange(
      localAppPlanIds.includes(id)
        ? localAppPlanIds.filter((x) => x !== id)
        : [...localAppPlanIds, id],
    );

  const toggleAddOn = (id: string) => {
    setLocalAddOnPlanIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSave = () => {
    onSave({ appPlanIds: localAppPlanIds, addOnPlanIds: localAddOnPlanIds });
    onClose();
  };

  // Gates the overlay AND every dismissal path, so the two can't drift.
  const busy = loading && appPlans.length === 0;

  const title: React.ReactNode | React.ReactNode[] = companyName
    ? [t("Plans"), companyName]
    : t("Plans");

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (busy) return;
        onClose();
      }}
      title={title}
      loading={busy}
      footer={
        <>
          <Button variant="default-outlined" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              localAppPlanIds.length === 0 || orphanedSelection.length > 0
            }
          >
            {t("Save")}
          </Button>
        </>
      }
    >
      <Stack>
        {orphanedSelection.length > 0 && (
          <Alert severity="warning" role="status">
            {t(
              "Some selected add-ons need an app that is not selected, remove them or add the app",
            )}
          </Alert>
        )}

        <Stack gap="var(--space-3)">
          <Stack gap="var(--space-1)">
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("Apps")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              {t("Select one or more apps this company has access to")}
            </Typography>
          </Stack>
          <Stack gap="var(--space-3)">
            {appPlans.length === 0 && !loading && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("No apps available")}
              </Typography>
            )}
            {appPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                title={cleanAppName(plan.name, plan.id)}
                description={plan.description}
                checked={localAppPlanIds.includes(plan.id)}
                onToggle={() => toggleApp(plan.id)}
              />
            ))}
          </Stack>
        </Stack>

        <Divider />

        <Stack gap="var(--space-3)">
          <Stack gap="var(--space-1)">
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("Add-ons")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              {t("Optional features bundled into add-on plans")}
            </Typography>
          </Stack>

          <Stack gap="var(--space-3)">
            {addOnPlans.length === 0 && !loading && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("No add-on plans available")}
              </Typography>
            )}
            {decoratedAddOns.map(({ plan, allowed, requiredAppNames }) => {
              const checked = localAddOnPlanIds.includes(plan.id);
              return (
                <PlanCard
                  key={plan.id}
                  title={plan.name || plan.id}
                  description={plan.description}
                  requires={allowed ? null : requiresLabel(requiredAppNames)}
                  checked={checked}
                  // A blocked add-on that is already selected stays clickable so a
                  // pre-existing orphan can still be removed; disabling only stops
                  // the owner picking up a new one.
                  disabled={!allowed && !checked}
                  onToggle={() => toggleAddOn(plan.id)}
                />
              );
            })}
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default PlanPickerDrawer;
